import { useRouter } from 'next/router';
import { datadogRum } from '@datadog/browser-rum';

import { FetchError } from '../../../helpers/fetch';
import { putLet2OwnRenewal } from '../../../helpers/renewal';
import {
  getRenewalEntryPoint,
  withRenewalEntryPoint,
} from '../../../helpers/renewalEntryPoint';
import { DirectDebitBalanceType } from '../../../types/finance/directDebit/directDebit';
import { LettingConfigEntry } from '../../../types/lettingConfig';
import { displayOneOffPayment } from '../../Let2OwnProgressStepper/helpers';

export const RENEWAL_COMPLETION_ERROR =
  "We couldn't complete your Let2offset renewal. Please try again, and if the problem persists contact your holiday home team.";

const LETTING_GRADE_NOT_APPROVED = 'LETTING_GRADE_NOT_APPROVED';

const getRenewalErrorMessage = (error: unknown): string =>
  error instanceof FetchError &&
  error.code === LETTING_GRADE_NOT_APPROVED &&
  error.message
    ? error.message
    : RENEWAL_COMPLETION_ERROR;

type UseRenewalSubmitArgs = {
  accountId?: number;
  currentDate: string;
  skipPayment?: boolean;
  lettingConfig: LettingConfigEntry[];
  setLoading?: (value: boolean) => void;
  setRenewalError?: (value: string | null) => void;
};

export const useRenewalSubmit = ({
  accountId,
  currentDate,
  skipPayment = false,
  lettingConfig,
  setLoading,
  setRenewalError,
}: UseRenewalSubmitArgs): (() => Promise<void>) => {
  const router = useRouter();

  return async () => {
    if (skipPayment && setLoading && setRenewalError && accountId) {
      setRenewalError(null);
      setLoading(true);
      try {
        await putLet2OwnRenewal(accountId);
      } catch (error) {
        datadogRum.addError(error, {
          context: 'let2own.renewal.complete',
        });
        setRenewalError(getRenewalErrorMessage(error));
        setLoading(false);
        return;
      }
      await router.push('/lettings-hub?L2ORenewalsConfirmation=default');
    } else {
      const nextStepPath = displayOneOffPayment(lettingConfig, currentDate)
        ? '/finance/partial-payment'
        : `/finance/direct-debit/setup?balanceType=${DirectDebitBalanceType.Let2Own}`;
      router.push(
        withRenewalEntryPoint(nextStepPath, getRenewalEntryPoint(router.query))
      );
    }
  };
};
